import Sidebar from "./Sidebar";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { LogOut, Sun, Moon, Menu, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { LayoutDashboard, Ticket, Users, Wrench, Package, ScrollText, ClipboardList, HardHat, ShieldCheck, CalendarClock, Radar, UserCog, FolderOpen, FileText, PackageOpen, Wallet, Cpu, Truck, ClipboardCheck, Settings, BellRing } from "lucide-react";
import GlobalSearch from "./GlobalSearch";
import PMReminderBell from "./PMReminderBell";
import ReminderNotifier from "./ReminderNotifier";

const NAV = [
  { to: "/app/dashboard",  label: "Dashboard",     icon: LayoutDashboard, roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/reminders",  label: "Reminders",     icon: BellRing,        roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/tickets",    label: "Service Calls", icon: Ticket,          roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/customers",  label: "Customers",     icon: Users,           roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/equipment",  label: "Equipment",     icon: Wrench,          roles: [] },
  { to: "/app/engineers",  label: "Engineers",     icon: HardHat,         roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/amc",        label: "AMC",           icon: ShieldCheck,     roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/pm",         label: "PM Schedule",   icon: CalendarClock,   roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/vendor-orders", label: "Supplier Daily Order", icon: Truck,   roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/delivery-challans", label: "Delivery Challan", icon: ClipboardCheck, roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/warranty",   label: "Vendor Warranty", icon: PackageOpen,   roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/spares",     label: "Spare Master",  icon: Cpu,             roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/expenses",   label: "Expenses",      icon: Wallet,          roles: ["super_admin","dispatcher","engineer"] },
  { to: "/app/offer-letters", label: "Offer Letters", icon: FileText,     roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/experience-letters", label: "Experience Letters", icon: FileText, roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/margin-calculator", label: "Margin Calculator", icon: ScrollText, roles: ["super_admin"] },
  { to: "/app/documents",  label: "Doc Drive",     icon: FolderOpen,      roles: ["super_admin","company_admin","dispatcher","engineer"] },
  { to: "/app/reports",    label: "Reports",       icon: ClipboardList,   roles: ["super_admin","company_admin","dispatcher"] },
  { to: "/app/users",      label: "Users",         icon: UserCog,         roles: ["super_admin"] },
  { to: "/app/settings",   label: "Settings",      icon: Settings,        roles: ["super_admin","company_admin"] },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      } else if (e.key === "Escape") {
        setSearchOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex h-screen bg-background">
      <ReminderNotifier />
      <Sidebar onOpenSearch={() => setSearchOpen(true)} />

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar with search — desktop */}
        <header className="hidden md:flex items-center justify-between border-b border-border/60 bg-card/40 px-4 py-2 backdrop-blur">
          <button
            data-testid="topbar-search-button"
            onClick={() => setSearchOpen(true)}
            className="inline-flex items-center gap-2 rounded-md border border-border/70 bg-secondary/30 px-3 py-1.5 text-xs text-muted-foreground w-80 hover:bg-secondary"
          >
            <Search className="h-3.5 w-3.5" />
            <span>Search anything…</span>
            <span className="ml-auto font-mono text-[10px] text-muted-foreground border border-border/70 rounded px-1 py-0.5">⌘K</span>
          </button>
          <PMReminderBell />
        </header>

        {/* Mobile top bar */}
        <header className="flex items-center justify-between border-b border-border/60 bg-card/40 px-4 py-3 backdrop-blur md:hidden">
          <div className="flex items-center gap-2">
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen((v) => !v)}
              className="rounded-md border border-border/70 p-2 text-muted-foreground"
            >
              <Menu className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
                <Radar className="h-4 w-4" />
              </div>
              <span className="font-heading text-base font-bold">HCIT-CallCRM</span>
            </div>
          </div>
          <div className="flex gap-1">
            <PMReminderBell />
            <button data-testid="mobile-search-button" onClick={() => setSearchOpen(true)} className="rounded-md border border-border/70 p-2">
              <Search className="h-4 w-4" />
            </button>
            <button data-testid="theme-toggle-mobile" onClick={toggle} className="rounded-md border border-border/70 p-2">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button data-testid="logout-mobile" onClick={handleLogout} className="rounded-md border border-border/70 p-2">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        {open && (
          <div className="border-b border-border/60 bg-card px-2 py-2 md:hidden">
            <div className="grid grid-cols-3 gap-1">
              {NAV.filter((it) => it.roles.includes(user?.role)).map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex flex-col items-center gap-1 rounded-md px-2 py-2 text-[11px] font-medium",
                      isActive ? "bg-primary/10 text-primary" : "text-muted-foreground",
                    )
                  }
                >
                  <it.icon className="h-4 w-4" />
                  <span>{it.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        )}

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
